import { IconProp, SizeProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleCheck,
  faCircleExclamation,
} from '@fortawesome/sharp-solid-svg-icons';
import { Placement } from '@popperjs/core';
import { ReactNode } from 'react';
import {
  Control,
  FieldError,
  RegisterOptions,
  ValidationRule,
} from 'react-hook-form';
import {
  OnChangeValue,
  OptionProps,
  Options,
  ValueContainerProps,
} from 'react-select';

import { BoundaryCDL } from '@/endpoints/itasca/comps/getBoundaryCDLOptions';
import { WaterDistrict } from '@/endpoints/itasca/comps/queryWaterDistricts';
import { County } from '@/endpoints/itasca/county/getCounties';
import { State } from '@/endpoints/itasca/state/getState';
import { ITownshipSection } from '@/endpoints/itasca/township/getSectionsInTownship';
import { ITownshipSearchResult } from '@/endpoints/itasca/township/getTownshipResults';

export type RangeValue = {
  min: number | string | undefined;
  max: number | string | undefined;
};

export type IFormFieldProps = {
  autoFocus?: boolean;
  additionalBlur?: () => void | undefined;
  additionalProps?: IAdditionalProps | undefined;
  className?: string;
  charCountShouldShow?: boolean;
  control?: Control<any>;
  dataTestId?: string;
  hidePlaceholder?: boolean;
  description?: string;
  disabled?: boolean;
  endAdornment?: (endAdornmentOptions?: EndAdornmentOptions) => ReactNode;
  error?: FieldError | undefined;
  focused?: boolean;
  helpText?: ReactNode;
  helpTextPlace?: Placement;
  allowNegative?: boolean;
  id?: string | undefined;
  inputProps?: any;
  label?: string;
  labelClassName?: string;
  name?: string | undefined;
  onBlur?: any;
  onChange?: any;
  startAdornment?: ReactNode;
  onInputChange?: any;
  onFocus?: any;
  options?: Options<ISelectOptionProps> | undefined;
  placeholder?: string;
  required?: boolean;
  rules?: FormValidationRules;
  size?: InputSize;
  showClearButton?: boolean;
  showEndAdornment?: boolean;
  borderShouldShow?: boolean;
  showStartAdornment?: boolean;
  hoverToolTip?: string;
  touched?: boolean;
  /** Used when a field cannot be modified by the user directly, but has a value assigned to it */
  isAutoCalculated?: boolean;
  type?: string;
  moreInfo?: string;
  value?:
    | string
    | number
    | string[]
    | undefined
    | OnChangeValue<ISelectOptionProps, false>
    | RangeValue
    | ICheckboxOptionProps[]
    | null
    | Object;

  // Below are for multiselect
  inputValue?: any;
  creatable?: boolean;
  async?: boolean;
  loadOptions?: (s) => Promise<ISelectOptionProps[]> | void;
  defaultOptions?: boolean | ISelectOptionProps[];
  cacheOptions?: boolean;
  showErrorMessage?: boolean;
};

type FloatingUiPlacement = `${'top' | 'right' | 'bottom' | 'left'}${
  | ''
  | '-start'
  | '-end'}`;

type ReactDatePickerProps = {
  dayClassName?: (date: Date) => string;
  openToDate?: Date;
  showCalendarIcon?: boolean;
  calendarIconClassName?: string;
  maxDate?: Date;
  minDate?: Date;
  popperPlacement?: FloatingUiPlacement;
  popperClassName?: string;
};

type IAdditionalProps = ReactDatePickerProps & {
  canPartialSearch?: boolean;
  clearIndicator?: () => ReactNode;
  maxLength?: number;
  multiSelect?: boolean;
  indeterminate?: boolean;
  isSearchabled?: boolean;
  placeholderValue?: any;
  rows?: number;
  showStepper?: boolean;
  optionItem?: (props: OptionProps<any>) => JSX.Element;
  valueContainer?: (props: ValueContainerProps<any>) => JSX.Element;
  decimalScale?: number;
  loadOptions?: (
    val: string,
    cb: (results: Options<ISelectOptionProps>[]) => void
  ) => void;
  containerClassName?: string;
  suffix?: string;
  hideDefault?: boolean;
  hideToolTip?: boolean;
  noThousandSeparator?: boolean;
  customMultiSelectValueResolver?: (val: any) => any;
};

export interface ITextInputProps {
  name: string;
  label?: string;
  helperText?: string;
  placeholder?: string;
  value?: string;
  onChange?: any;
  control?: Control<any>;
  rules?: FormValidationRules;
  showClearButton?: boolean;
}

export interface ICountyFip {
  fips: string;
  fullname: string;
  name: string;
  state_abbreviation: string;
  state_fips: string;
}

export interface IStateFip {
  state_abbreviation: string;
  fullname: string;
  state_fips: string;
}

export interface ISelectOptionProps {
  value?:
    | string
    | ITownshipSearchResult
    | ICountyFip
    | IStateFip
    | ITownshipSection
    | BoundaryCDL
    | WaterDistrict
    | County
    | State
    | Object
    | number[]
    | number;
  label: string | ReactNode;
  icon?: string | IconProp | ReactNode;
  id?: string;
}
export interface ICheckboxOptionProps<T extends string = string> {
  value: T;
  label: string;
}

export interface ISelectInputProps extends Omit<ITextInputProps, 'value'> {
  value?: string | string[];
  options?: ISelectOptionProps[];
}
export interface IMultiSelectInputProps extends ISelectInputProps {
  isSquare?: boolean;
  isMultiSelectBox: boolean;
}
export interface IRadioInputProps extends ISelectInputProps {
  isSquare?: boolean;
  isTraditionalRadio: boolean;
}
export interface FormValidationRules
  extends Omit<
    RegisterOptions<any>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  > {
  min?: ValidationRule<number>;
  max?: ValidationRule<number>;
}

export type EndAdornmentOptions = {
  className?: string;
  size?: SizeProp;
};

export enum Status {
  'NONE' = 'none',
  'SUCCESS' = 'success',
  'ERROR' = 'error',
  'WARNING' = 'warning',
  'LOADING' = 'loading',
  'FOCUSED' = 'focused',
}

export const statusIcons = {
  [Status.SUCCESS]: {
    color: '#2E7A56',
    icon: faCircleCheck,
  },
  [Status.ERROR]: {
    color: '#D91F11',
    icon: faCircleExclamation,
  },
};

export enum InputSize {
  SMALL,
  MEDIUM,
  LARGE,
}
